import DOMPurify from 'dompurify'

const parseNumeric = (value, fallback = 0) => {
    if (value == null || value === '') return fallback
    // Strip out non-numeric characters (e.g. "$10.50a_" -> "10.50") so formatted values can be parsed.
    const cleaned = String(value).replace(/[^\d.-]/g, '')
    const parsed = Number(cleaned)
    // If the cleaned value can't be parsed into a finite number, return the fallback so sorting stays stable.
    return Number.isFinite(parsed) ? parsed : fallback
}

const formatNumeric = (value, sign = null) => {
    if (value == null || value === '') {
        return ''
    }
    const numericValue = parseNumeric(value)
    if (!Number.isFinite(numericValue)) {
        return ''
    }

    if (sign === 'money') {
        return `$ ${numericValue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    if (sign === 'percent') {
        return `${numericValue.toLocaleString(undefined, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        })} %`
    }

    if (sign === 'percentWhole') {
        const roundedDisplayValue = Number(numericValue.toFixed(1))
        return `${roundedDisplayValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}%`
    }

    return numericValue.toLocaleString()
}

const hasNumericText = (value) => {
    return typeof value !== 'string' || /\d/.test(value)
}

const formatMoneyValue = (value) => {
    if (!hasNumericText(value)) {
        return value
    }

    const numericValue = parseNumeric(value, null)
    if (!Number.isFinite(numericValue)) {
        return value
    }

    return formatNumeric(numericValue, 'money')
}

const formatMarginValue = (value) => {
    if (!hasNumericText(value)) {
        return value
    }

    const numericValue = parseNumeric(value, null)
    if (!Number.isFinite(numericValue)) {
        return value
    }

    return `${numericValue.toFixed(2)}%`
}

const normalizePart = (value) => {
    if (value == null) return ''
    return value
        .toString()
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, '')
}

const humanizeKey = (value) => {
    if (value == null) return ''
    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

const formatBooleanLabel = (value, trueLabel = 'Yes', falseLabel = 'No') => {
    if (typeof value !== 'boolean') {
        return value
    }
    return value ? trueLabel : falseLabel
}

const formatDateString = (value, fallback = '') => {
    if (!value) {
        return fallback
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return fallback
    }

    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const year = date.getUTCFullYear()

    return `${year}-${month}-${day}`
}

const normalizeLooseText = (value) => {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
}

const toPositiveNumberOrNull = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const toNonNegativeNumberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

function decodeEscapedMarkup(value) {
    const text = String(value || '')
    if (!/&lt;\/?[a-z][\s\S]*?&gt;/i.test(text)) return text
    const decoder = document.createElement('textarea')
    decoder.innerHTML = text
    return decoder.value
}

// Keep document formatting, but never vendor CSS, event handlers or resources.
function descriptionHtml(value) {
    return DOMPurify.sanitize(decodeEscapedMarkup(value), {
        ALLOWED_TAGS: [
            'p',
            'br',
            'div',
            'span',
            'strong',
            'b',
            'em',
            'i',
            'u',
            's',
            'ul',
            'ol',
            'li',
            'table',
            'thead',
            'tbody',
            'tfoot',
            'tr',
            'th',
            'td',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'blockquote',
            'sub',
            'sup',
        ],
        ALLOWED_ATTR: [],
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
    })
}

// Parse in an inert template; the editor and collapsed preview use plain text.
function descriptionText(value) {
    if (!value) return ''

    const template = document.createElement('template')
    template.innerHTML = descriptionHtml(value)

    // Preserve boundaries between paragraphs, line breaks, and table cells while
    // leaving inline formatting (e.g. <strong>part</strong>number) intact.
    template.content
        .querySelectorAll('br, p, div, li, tr, td, th, h1, h2, h3, h4, h5, h6, hr, section, ul, ol')
        .forEach((element) => {
            element.before(document.createTextNode(' '))
            element.after(document.createTextNode(' '))
        })

    return (template.content.textContent || '').replace(/\s+/g, ' ').trim()
}

export {
    descriptionHtml,
    descriptionText,
    formatDateString,
    formatBooleanLabel,
    formatMarginValue,
    formatMoneyValue,
    formatNumeric,
    humanizeKey,
    normalizeLooseText,
    normalizePart,
    parseNumeric,
    toNonNegativeNumberOrNull,
    toPositiveNumberOrNull,
}
